import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { AuthenticationManagerOperation } from '../../../../shared/operations/auth/authentication-manager.operation';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthApiService } from '../../../services/auth.api.service';
import { UserState } from '../../../states/user-state';
import { BrowserStorageService } from '../../../services/browser-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-reset-password-email',
    templateUrl: './reset-password-email.component.html',
    styleUrl: './reset-password-email.component.scss',
    standalone: false
})
export class ResetPasswordEmailComponent {
    email: string = '';
    password: string = '';
    hide = true;
    keepLoggedIn = false;
    quote: string = '';
    authenticationManager: AuthenticationManagerOperation;
    @ViewChild('emailInput') emailInputElement!: ElementRef;
    fg: FormGroup;
    isSubmitting = false;
    message: string | null = null;
    error: string | null = null;

    constructor(
        private authService: AuthApiService,
        private userState: UserState,
        private router: Router,
        private route: ActivatedRoute,
        private browserStorageService: BrowserStorageService, // Inject LocalStorageService
        private fb: FormBuilder
    ) {
        this.authenticationManager = new AuthenticationManagerOperation(this.authService, userState, router, browserStorageService);
        this.fg = this.fb.group({
            email: this.fb.nonNullable.control('', Validators.required),
        });
    }

    ngAfterViewInit() {
        // Backward-compatible: if an emailed link hits `/reset-password?token=...`,
        // redirect to the token-based reset route.
        const token = this.route.snapshot.queryParamMap.get('token');
        if (token) {
            this.router.navigate(['/reset'], { queryParams: { token } });
            return;
        }
        if (this.emailInputElement) {
            this.emailInputElement.nativeElement.focus();
        }
    }

    requestResetEmail(): void {
        this.message = null;
        this.error = null;

        if (this.fg.invalid) {
            this.fg.markAllAsTouched();
            return;
        }

        const email = String(this.emailControl.value || '');
        this.isSubmitting = true;

        this.authService
            .forgotPassword(email)
            .pipe(finalize(() => (this.isSubmitting = false)))
            .subscribe({
                next: (res) => {
                    this.message = res?.message || 'If an account exists for that email, a reset link has been sent.';
                },
                error: (err) => {
                    console.error('Forgot password failed', err);
                    this.error = 'Unable to request password reset right now.';
                },
            });
    }

    clickEvent(event: MouseEvent): void {
        this.hide = !this.hide;
        event.stopPropagation();
    }

    get emailControl() {
        return this.fg.get('email') as FormControl;
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent): void {
        this.requestResetEmail();
    }

    onResetPasswordClick = (_e: Event) => {
        this.requestResetEmail();
    };
}
